import React, { useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet, Text, TouchableOpacity } from './rn';
import { ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import GlassControlButton from './GlassControlButton';

type Props = {
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
  /** Ekranlar kendi geri kontrollerini sınamaya devam edebilsin diye geçirilir. */
  testID?: string;
  /**
   * Kendi cam yuvarlağını çizmez (7.14 · D-550).
   *
   * Cam bir gezinme bandının üstünde duruyorsa gerekir: cam üstüne cam,
   * yüzeyin sınırını iki kez çizmek olur ve iOS bunu yapmıyor — bandın kendisi
   * camsa içindeki geri kontrolü çıplak bir işarettir. Dokunma alanı aynı
   * kalır, yalnız yüzey kalkar.
   */
  plain?: boolean;
};

// Mesaj başlığındaki geri kontrolü uygulamanın tek görsel kaynağıdır.
// Profil, cüzdan ve onboarding gibi diğer üst-seviye sayfalar da bu bileşeni
// kullanır; böylece ölçü, sembol, cam yüzey ve erişilebilirlik birlikte kalır.
//
// Cam yüzeyin kendisi artık burada tanımlı değil: GlassControlButton tek kaynak
// (7.14 / D-506). Bu bileşen yalnız kendi sembolünü ve etiketini taşır.
export default function HeaderBackButton({ onPress, style, testID, plain }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const symbol = <Text style={styles.symbol}>‹</Text>;

  if (plain) {
    return (
      <TouchableOpacity
        testID={testID}
        style={[styles.plain, style]}
        onPress={onPress}
        activeOpacity={0.65}
        accessible
        accessibilityRole="button"
        accessibilityLabel={t('common.back')}
        hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
        {symbol}
      </TouchableOpacity>
    );
  }

  return (
    <GlassControlButton testID={testID} onPress={onPress} accessibilityLabel={t('common.back')} style={style}>
      {symbol}
    </GlassControlButton>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  /** Yüzeysiz hâl: ölçü ve dokunma alanı aynı, boya yok. */
  plain: {
    width: 40,
    height: 40,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  symbol: {
    fontSize: 32,
    lineHeight: 32,
    color: colors.black,
    textAlign: 'center',
    transform: [{ translateY: -1 }],
  },
});
